import { MediaAssetInput } from '../../components/settings/MediaAssetPicker';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import NotificationSnackbar from '../../components/ui/NotificationSnackbar';
import { useStorefrontCms } from '../../services/fusionBoxCmsService';

const iconPresets = [
  ['mdi:shield-check-outline', 'Secure payment'],
  ['mdi:chef-hat', 'Freshly prepared'],
  ['mdi:truck-fast-outline', 'Fast delivery'],
  ['mdi:calendar-remove-outline', 'Easy cancellation'],
  ['mdi:leaf-circle-outline', 'Vegetarian / Jain'],
  ['mdi:star-outline', 'Reviews'],
  ['mdi:check-decagram-outline', 'Verified'],
];

const blankTrust = {
  label: '',
  short_label: '',
  icon_name: 'mdi:shield-check-outline',
  icon_url: '',
  pages: ['all'],
  display_order: 100,
  is_active: true,
};

const apiBase = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const assetUrl = (value) => {
  const url = String(value || '');
  if (!url || /^https?:|^data:|^blob:/i.test(url)) return url;
  return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/${url}`;
};

export default function SiteSettings() {
  const { createTrustItem, deleteTrustItem, fetchSiteSettings, fetchTrustItems, updateSiteSettings, updateTrustItem, uploadCmsImage } = useStorefrontCms();
  const [settings, setSettings] = useState({});
  const [trustItems, setTrustItems] = useState([]);
  const [newTrust, setNewTrust] = useState(blankTrust);
  const [areasText, setAreasText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const uploadTarget = useRef(null);
  const uploadRef = useRef(null);
  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });

  const patch = (path, value) => setSettings((current) => {
    const next = typeof structuredClone === 'function' ? structuredClone(current) : JSON.parse(JSON.stringify(current || {}));
    let cursor = next;
    path.slice(0, -1).forEach((key) => { cursor[key] = cursor[key] || {}; cursor = cursor[key]; });
    cursor[path[path.length - 1]] = value;
    return next;
  });

  const load = async () => {
    setLoading(true);
    try {
      const [settingsResult, trustResult] = await Promise.all([fetchSiteSettings(), fetchTrustItems()]);
      const next = settingsResult?.settings || {};
      setSettings(next);
      setAreasText((next.areas || []).join(', '));
      setTrustItems((Array.isArray(trustResult) ? trustResult : trustResult?.items || []).map((item) => ({
        ...item,
        pages: Array.isArray(item.pages) && item.pages.length ? item.pages : ['all'],
      })));
    } catch (error) {
      notify(error?.response?.data?.error || 'Failed to load website settings', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, areas: areasText.split(',').map((item) => item.trim()).filter(Boolean) };
      const result = await updateSiteSettings(payload);
      setSettings(result?.settings || payload);
      notify('Website settings saved.');
    } catch (error) {
      notify(error?.response?.data?.error || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const patchTrust = (index, key, value) => setTrustItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  const saveTrust = async (item) => {
    try {
      await updateTrustItem(item.id, { ...item, is_active: item.is_active ? 1 : 0, display_order: Number(item.display_order || 0) });
      notify('Trust icon updated.');
      await load();
    } catch (error) {
      notify(error?.response?.data?.error || 'Failed to update trust icon', 'error');
    }
  };
  const addTrust = async () => {
    if (!newTrust.label.trim()) return notify('Add a trust icon label first.', 'warning');
    try {
      await createTrustItem({ ...newTrust, is_active: newTrust.is_active ? 1 : 0, display_order: Number(newTrust.display_order || 0) });
      setNewTrust(blankTrust);
      notify('Trust icon added.');
      await load();
    } catch (error) {
      notify(error?.response?.data?.error || 'Failed to add trust icon', 'error');
    }
  };
  const removeTrust = async (id) => {
    try {
      await deleteTrustItem(id);
      notify('Trust icon deleted.');
      await load();
    } catch (error) {
      notify(error?.response?.data?.error || 'Failed to delete trust icon', 'error');
    }
  };
  const uploadTrustIcon = async (file) => {
    if (!file || !uploadTarget.current) return;
    try {
      const result = await uploadCmsImage(file);
      const url = result?.url || result?.image_url || '';
      if (uploadTarget.current.type === 'new') setNewTrust((item) => ({ ...item, icon_url: url }));
      else patchTrust(uploadTarget.current.index, 'icon_url', url);
      notify('Icon uploaded. Save the trust item to publish it.');
    } catch (error) {
      notify(error?.response?.data?.error || 'Icon upload failed', 'error');
    }
  };
  const chooseUpload = (target) => {
    uploadTarget.current = target;
    uploadRef.current?.click();
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}><CircularProgress /></Box>;

  const instagram = settings.instagram || {};
  const cookie = settings.cookie_consent || {};
  const tracking = settings.tracking || {};

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box><Typography variant="h4" fontWeight={900}>Global Website Settings</Typography><Typography color="text.secondary">Shared integrations, tracking, service areas and trust icons.</Typography></Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={saveSettings} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</Button>
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box><Typography variant="h6" fontWeight={900}>Instagram Connection</Typography><Typography variant="body2" color="text.secondary">Connect the professional account used by live Instagram modules.</Typography></Box>
              <Switch checked={!!instagram.enabled} onChange={(event) => patch(['instagram', 'enabled'], event.target.checked)} />
            </Stack>
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={12} md={3}><TextField label="Account handle" value={instagram.account_handle || ''} onChange={(event) => patch(['instagram', 'account_handle'], event.target.value)} fullWidth /></Grid>
              <Grid item xs={12} md={3}><TextField label="Instagram User ID" value={instagram.instagram_user_id || ''} onChange={(event) => patch(['instagram', 'instagram_user_id'], event.target.value)} fullWidth /></Grid>
              <Grid item xs={12} md={3}><TextField label="Access token" type="password" value={instagram.access_token || ''} onChange={(event) => patch(['instagram', 'access_token'], event.target.value)} fullWidth /></Grid>
              <Grid item xs={6} md={1.5}><TextField label="API version" value={instagram.api_version || 'v25.0'} onChange={(event) => patch(['instagram', 'api_version'], event.target.value)} fullWidth /></Grid>
              <Grid item xs={6} md={1.5}><TextField label="Cache (min)" type="number" value={instagram.cache_minutes || 30} onChange={(event) => patch(['instagram', 'cache_minutes'], Number(event.target.value || 30))} fullWidth /></Grid>
            </Grid>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="h6" fontWeight={900}>Cookie and Tracking</Typography>
            <FormControlLabel control={<Switch checked={!!cookie.enabled} onChange={(event) => patch(['cookie_consent', 'enabled'], event.target.checked)} />} label="Show cookie consent" />
            <TextField label="Cookie message" value={cookie.message || ''} onChange={(event) => patch(['cookie_consent', 'message'], event.target.value)} fullWidth multiline rows={2} margin="dense" />
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}><TextField label="GTM ID" value={tracking.gtm_id || ''} onChange={(event) => patch(['tracking', 'gtm_id'], event.target.value)} fullWidth margin="dense" /></Grid>
              <Grid item xs={12} md={4}><TextField label="GA4 ID" value={tracking.ga4_id || ''} onChange={(event) => patch(['tracking', 'ga4_id'], event.target.value)} fullWidth margin="dense" /></Grid>
              <Grid item xs={12} md={4}><TextField label="Meta Pixel ID" value={tracking.meta_pixel_id || ''} onChange={(event) => patch(['tracking', 'meta_pixel_id'], event.target.value)} fullWidth margin="dense" /></Grid>
            </Grid>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="h6" fontWeight={900}>Service Areas</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Comma-separated areas shown across the website.</Typography>
            <TextField label="Areas served" value={areasText} onChange={(event) => setAreasText(event.target.value)} fullWidth multiline rows={3} />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card><CardContent>
            <Typography variant="h6" fontWeight={900}>Global Trust Icons</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choose a preset icon or an image from Media. Add the Global Trust Icons module in the page builder wherever this content should appear.</Typography>
            <MediaAssetInput ref={uploadRef} onSelect={asset => { if (!uploadTarget.current) return; if (uploadTarget.current.type === 'new') setNewTrust(item => ({ ...item, icon_url: asset.url })); else patchTrust(uploadTarget.current.index, 'icon_url', asset.url); }} />
            <Stack spacing={2}>
              {trustItems.map((item, index) => (
                <Box key={item.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}><TextField label="Label" value={item.label || ''} onChange={(event) => patchTrust(index, 'label', event.target.value)} fullWidth size="small" /></Grid>
                    <Grid item xs={12} md={2}><TextField label="Short label" value={item.short_label || ''} onChange={(event) => patchTrust(index, 'short_label', event.target.value)} fullWidth size="small" /></Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small"><InputLabel>Preset icon</InputLabel><Select label="Preset icon" value={item.icon_name || ''} onChange={(event) => patchTrust(index, 'icon_name', event.target.value)}>{iconPresets.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {item.icon_url ? <img src={assetUrl(item.icon_url)} alt="" style={{ width: 34, height: 34, objectFit: 'contain' }} /> : null}
                        <Button size="small" variant="outlined" onClick={() => chooseUpload({ type: 'existing', index })}>Choose image</Button>
                      </Stack>
                    </Grid>
                    <Grid item xs={6} md={1}><TextField label="Order" value={item.display_order || 0} type="number" onChange={(event) => patchTrust(index, 'display_order', event.target.value)} fullWidth size="small" /></Grid>
                    <Grid item xs={6} md={1}><Switch checked={!!item.is_active} onChange={(event) => patchTrust(index, 'is_active', event.target.checked)} /></Grid>
                    <Grid item xs={12} md={1}><IconButton color="error" onClick={() => removeTrust(item.id)}><DeleteIcon /></IconButton></Grid>
                  </Grid>
                  <Button size="small" variant="contained" sx={{ mt: 1.5 }} onClick={() => saveTrust(item)}>Save Trust Icon</Button>
                </Box>
              ))}

              <Box sx={{ border: '1px dashed #ffb17f', bgcolor: '#fffaf6', borderRadius: 2, p: 2 }}>
                <Typography fontWeight={800} sx={{ mb: 1.5 }}>Add Trust Icon</Typography>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={3}><TextField label="Label" value={newTrust.label} onChange={(event) => setNewTrust((item) => ({ ...item, label: event.target.value }))} fullWidth size="small" /></Grid>
                  <Grid item xs={12} md={2}><TextField label="Short label" value={newTrust.short_label} onChange={(event) => setNewTrust((item) => ({ ...item, short_label: event.target.value }))} fullWidth size="small" /></Grid>
                  <Grid item xs={12} md={2}><FormControl fullWidth size="small"><InputLabel>Preset icon</InputLabel><Select label="Preset icon" value={newTrust.icon_name} onChange={(event) => setNewTrust((item) => ({ ...item, icon_name: event.target.value }))}>{iconPresets.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={12} md={2}><Button size="small" variant="outlined" onClick={() => chooseUpload({ type: 'new' })}>{newTrust.icon_url ? 'Change image' : 'Choose image'}</Button></Grid>
                  <Grid item xs={6} md={1}><TextField label="Order" type="number" value={newTrust.display_order} onChange={(event) => setNewTrust((item) => ({ ...item, display_order: event.target.value }))} fullWidth size="small" /></Grid>
                  <Grid item xs={6} md={1}><Switch checked={newTrust.is_active} onChange={(event) => setNewTrust((item) => ({ ...item, is_active: event.target.checked }))} /></Grid>
                  <Grid item xs={12} md={1}><Button startIcon={<AddIcon />} onClick={addTrust}>Add</Button></Grid>
                </Grid>
              </Box>
            </Stack>
          </CardContent></Card>
        </Grid>
      </Grid>
      <NotificationSnackbar open={notification.open} message={notification.message} severity={notification.severity} onClose={() => setNotification((value) => ({ ...value, open: false }))} />
    </Box>
  );
}
